<template>
  <div class="theme-tagging page-shell">
    <!-- 顶部：KPI + 筛选 -->
    <div class="bench-header">
      <div class="kpi-row">
        <div class="kpi-cell">
          <span class="kpi-label">Top500 池</span>
          <span class="kpi-value">{{ stats.total || 0 }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">已打标</span>
          <span class="kpi-value kpi-value--accent">{{ stats.tagged || 0 }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">标签总数</span>
          <span class="kpi-value">{{ stats.tags || 0 }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">生效题材</span>
          <span class="kpi-value">{{ stats.themes || 0 }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">景气度报告期</span>
          <span class="kpi-value kpi-value--plain">{{ prosperityPeriod || '无' }}</span>
        </div>
        <div class="kpi-spacer" />
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadData"
        >刷新</el-button>
      </div>
      <div class="filter-row">
        <el-input
          v-model="search"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索代码 / 名称"
          style="width: 200px"
        />
        <el-select
          v-model="themeFilter"
          size="small"
          clearable
          placeholder="按题材筛选"
          style="width: 160px"
        >
          <el-option
            v-for="t in activeDict"
            :key="t.id"
            :label="t.name"
            :value="t.id"
          />
        </el-select>
        <el-checkbox v-model="onlyTagged">仅看已打标</el-checkbox>
        <span class="filter-tip">点击行选中个股，在右侧面板打标</span>
      </div>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <!-- 主体：左列表 + 右打标面板 -->
    <div class="bench-body" v-loading="loading" element-loading-text="加载 Top500 池...">
      <div class="stock-panel">
        <el-table
          :data="filteredStocks"
          height="100%"
          size="mini"
          stripe
          highlight-current-row
          :row-style="{ cursor: 'pointer' }"
          @current-change="onCurrentChange"
        >
          <el-table-column prop="rank" label="#" width="48" />
          <el-table-column prop="code" label="代码" width="76">
            <template slot-scope="{ row }">
              <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer" :underline="false">
                {{ row.code }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="名称" width="94" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer" :underline="false">
                {{ row.name }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column prop="industry" label="行业" width="88" show-overflow-tooltip />
          <el-table-column label="题材标签" min-width="200">
            <template slot-scope="{ row }">
              <div class="theme-chips">
                <el-tag
                  v-for="t in row.themes"
                  :key="t.themeId"
                  size="mini"
                  effect="plain"
                  class="theme-chip"
                >{{ t.themeName }}</el-tag>
                <span v-if="!row.themes.length" class="theme-none">--</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="景气度" width="150" align="center">
            <template slot-scope="{ row }">
              <div v-if="row.prosperity" class="prosp-tags">
                <el-tooltip
                  v-for="m in prosperityMeta"
                  :key="m.key"
                  :content="prospTooltip(row, m.key)"
                  placement="top"
                >
                  <span :class="prospTagClass(row, m.key)">{{ m.short }}</span>
                </el-tooltip>
              </div>
              <span v-else class="prosp-none">--</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="64" align="center">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" @click.stop="selectStock(row)">打标</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 右侧打标面板 -->
      <div class="side-panel">
        <div v-if="!current" class="side-empty">
          <i class="el-icon-price-tag" />
          <p>从左侧点击一只个股<br>开始打标</p>
        </div>

        <template v-else>
          <!-- 个股信息卡 -->
          <div class="side-card stock-card">
            <div class="stock-head">
              <div class="stock-title">
                <span class="stock-name">{{ current.name }}</span>
                <span class="stock-code">{{ current.code }}</span>
              </div>
              <el-tag size="mini" type="info" effect="plain">{{ current.industry || '未知行业' }}</el-tag>
            </div>
            <div class="stock-meta">
              <span>排名 #{{ current.rank }}</span>
              <span>持仓 {{ current.shares }} 家</span>
              <span>市值 {{ current.marketValue }} 亿</span>
            </div>
            <div v-if="current.prosperity" class="prosp-tags prosp-tags--left">
              <el-tooltip
                v-for="m in prosperityMeta"
                :key="m.key"
                :content="prospTooltip(current, m.key)"
                placement="top"
              >
                <span :class="prospTagClass(current, m.key)">{{ m.label }}</span>
              </el-tooltip>
            </div>
          </div>

          <!-- 题材标签：已有标签 + 添加表单合并为一张卡，压缩右栏高度 -->
          <div class="side-card">
            <div class="card-title">题材标签<em v-if="current.themes.length">{{ current.themes.length }}</em></div>
            <div v-if="current.themes.length" class="owned-tags">
              <el-tooltip
                v-for="t in current.themes"
                :key="t.themeId"
                placement="top"
              >
                <div slot="content">
                  {{ t.themeName }} · {{ t.source }} · {{ t.operator }}<br />
                  {{ t.note }}<br />
                  {{ t.createdAt }}
                </div>
                <el-tag
                  size="small"
                  closable
                  class="owned-tag"
                  @close="removeTag(t)"
                >{{ t.themeName }}</el-tag>
              </el-tooltip>
            </div>
            <div v-else class="owned-empty">尚无题材标签</div>

            <div class="card-title card-title--gap">添加题材<span class="card-required">*</span></div>
            <el-select
              v-model="form.themeIds"
              multiple
              size="small"
              filterable
              collapse-tags
              placeholder="选择一个或多个题材"
              style="width: 100%"
              :disabled="!activeDict.length"
            >
              <el-option
                v-for="t in activeDict"
                :key="t.id"
                :label="`${t.name}（${t.category}）`"
                :value="t.id"
              />
            </el-select>
            <div v-if="!activeDict.length" class="form-hint">
              题材字典为空，请先到「题材字典」页创建
            </div>

            <div class="card-title card-title--gap card-title--row">
              <span>打标依据<span class="card-required">*</span></span>
              <el-select v-model="form.source" size="mini" style="width: 110px">
                <el-option v-for="s in sources" :key="s" :label="s" :value="s" />
              </el-select>
            </div>

            <el-input
              v-model="form.note"
              type="textarea"
              :rows="2"
              maxlength="200"
              show-word-limit
              placeholder="必填：说明该股属于此题材的依据（公告/研报出处、业务占比等）"
            />

            <el-button
              type="primary"
              size="small"
              style="width: 100%; margin-top: 10px"
              :loading="submitting"
              :disabled="!form.themeIds.length || !form.note.trim()"
              @click="submitTags"
            >
              {{ form.themeIds.length > 1 ? `打标 ${form.themeIds.length} 个题材` : '确认打标' }}
            </el-button>
          </div>
        </template>

        <!-- 最近打标动态 -->
        <div class="side-card log-card">
          <div class="card-title">最近打标动态</div>
          <div class="log-list">
            <div v-if="!logs.length" class="owned-empty">暂无记录</div>
            <div v-for="log in logs" :key="log.id" class="log-item">
              <span :class="['log-dot', `log-dot--${log.action}`]" />
              <div class="log-main">
                <div class="log-line">
                  <span class="log-action">{{ actionCn(log.action) }}</span>
                  <span class="log-target">{{ logTarget(log) }}</span>
                </div>
                <div class="log-sub">
                  <span v-if="log.detail && log.detail.source" class="log-src">{{ log.detail.source }}</span>
                  <span class="log-meta">{{ log.operator }} · {{ shortTime(log.createdAt) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'
import { formatFactorValue } from '../utils/factorFormat.js'

const PROSPERITY_META = [
  { key: 'npYoy', short: '成长', label: '成长', fk: 'np_yoy' },
  { key: 'npAccel', short: '加速', label: '加速', fk: 'np_accel' },
  { key: 'cfq', short: '现金流', label: '现金流', fk: 'cfq' },
]
const TAG_CN = { positive: '正向', negative: '负向', neutral: '中性' }
const ACTION_CN = {
  add: '添加', remove: '移除', create: '新建', update: '编辑',
  merge: '合并', deactivate: '停用', activate: '启用',
}

export default {
  name: 'ThemeTagging',
  data() {
    return {
      loading: false,
      submitting: false,
      error: '',
      list: [],
      dict: [],
      stats: {},
      prosperityPeriod: '',
      sources: [],
      logs: [],
      search: '',
      themeFilter: null,
      onlyTagged: false,
      current: null,
      form: { themeIds: [], source: '研报', note: '' },
      prosperityMeta: PROSPERITY_META,
    }
  },
  computed: {
    activeDict() {
      return this.dict.filter((d) => d.status === 'active')
    },
    filteredStocks() {
      let rows = this.list
      const kw = this.search.trim().toLowerCase()
      if (kw) {
        rows = rows.filter((s) => s.code.includes(kw) || (s.name || '').toLowerCase().includes(kw))
      }
      if (this.themeFilter) {
        rows = rows.filter((s) => s.themes.some((t) => t.themeId === this.themeFilter))
      }
      if (this.onlyTagged) {
        rows = rows.filter((s) => s.themes.length > 0)
      }
      return rows
    },
  },
  watch: {
    filteredStocks(rows) {
      // 当前选中行被筛掉时清空选中，避免右面板停留在不可见个股上
      if (this.current && !rows.some((r) => r.code === this.current.code)) {
        this.current = null
      }
    },
  },
  async created() {
    await Promise.all([this.loadData(), this.loadLogs()])
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    async loadData() {
      this.loading = true
      this.error = ''
      try {
        const data = await request('/api/theme-tag/stocks')
        this.list = data.list || []
        this.dict = data.dict || []
        this.stats = data.stats || {}
        this.prosperityPeriod = data.prosperityPeriod || ''
        this.sources = data.sources || []
        if (this.sources.length && !this.sources.includes(this.form.source)) {
          this.form.source = this.sources[0]
        }
        // 刷新后用最新数据恢复选中行
        if (this.current) {
          const fresh = this.list.find((s) => s.code === this.current.code)
          this.current = fresh || null
        }
      } catch (e) {
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    async loadLogs() {
      try {
        const data = await request('/api/theme-tag/logs?limit=50')
        this.logs = data.list || []
      } catch {
        // 审计流加载失败不打断主流程
      }
    },
    onCurrentChange(row) {
      this.current = row || null
    },
    selectStock(row) {
      this.current = row
    },
    async submitTags() {
      if (!this.current) return
      const note = this.form.note.trim()
      if (!note) {
        this.$message.warning('请填写打标依据')
        return
      }
      if (!this.form.themeIds.length) {
        this.$message.warning('请选择题材')
        return
      }
      this.submitting = true
      try {
        for (const themeId of this.form.themeIds) {
          await request('/api/theme-tag', {
            method: 'POST',
            body: JSON.stringify({
              code: this.current.code,
              themeId,
              source: this.form.source,
              note,
              reportPeriod: this.prosperityPeriod,
            }),
          })
        }
        this.$message.success(`已为「${this.current.name}」打标 ${this.form.themeIds.length} 个题材`)
        this.form.themeIds = []
        this.form.note = ''
        await this.loadData()
        await this.loadLogs()
      } catch (e) {
        this.$message.error(e.message || String(e))
      } finally {
        this.submitting = false
      }
    },
    removeTag(theme) {
      if (!this.current) return
      this.$confirm(
        `确定移除「${this.current.name}」的「${theme.themeName}」标签吗？`,
        '移除标签',
        { type: 'warning' },
      ).then(async () => {
        try {
          const data = await request('/api/theme-tag', {
            method: 'DELETE',
            body: JSON.stringify({ code: this.current.code, themeId: theme.themeId }),
          })
          if (data.logs) this.logs = data.logs
          this.$message.success('已移除')
          await this.loadData()
        } catch (e) {
          this.$message.error(e.message || String(e))
        }
      }).catch(() => {})
    },
    actionCn(action) {
      return ACTION_CN[action] || action
    },
    logTarget(log) {
      if (log.action === 'add' || log.action === 'remove') {
        return `${log.stockName || log.code} · ${log.themeName}`
      }
      if (log.action === 'merge' && log.detail) {
        return `${log.detail.from} → ${log.themeName}（迁移 ${log.detail.moved} 只）`
      }
      return log.themeName || ''
    },
    shortTime(s) {
      return String(s || '').slice(5, 16)
    },
    prospTagOf(row, key) {
      return (row.prosperity && row.prosperity[key]) || null
    },
    prospTagClass(row, key) {
      const t = this.prospTagOf(row, key)
      return ['prosp-tag', `prosp-tag--${t ? t.tag : 'none'}`]
    },
    prospTooltip(row, key) {
      const meta = PROSPERITY_META.find((m) => m.key === key)
      const t = this.prospTagOf(row, key)
      if (!t) return `${meta.label}：无数据`
      return `${meta.label} ${formatFactorValue(meta.fk, t.value)}（全市场分位 ${t.pct}，${TAG_CN[t.tag] || t.tag}）`
    },
  },
}
</script>

<style scoped>
/* 整页固定为视口高度：头部（KPI+筛选）占位，主体自适应，
   滚动只发生在表格内部与右侧面板内部，页面本身不产生纵向滚动。
   96px = 顶部 header 56px + .app-main 上下 padding 各 20px */
.theme-tagging {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 96px);
  min-height: 480px;
  box-sizing: border-box;
  text-align: left;
}

.bench-header {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 10px;
}

.kpi-row {
  display: flex;
  align-items: center;
  gap: 28px;
}

.kpi-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kpi-label {
  font-size: 12px;
  color: #909399;
}

.kpi-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.kpi-value--accent {
  color: #f56c6c;
}

.kpi-value--plain {
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  padding-top: 3px;
}

.kpi-spacer {
  flex: 1;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
}

.filter-tip {
  font-size: 12px;
  color: #c0c4cc;
}

.bench-body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 10px;
}

.stock-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.side-panel {
  flex: 0 0 336px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.side-panel::-webkit-scrollbar {
  width: 6px;
}

.side-panel::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

.side-empty {
  background: #fff;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  padding: 48px 0;
  text-align: center;
  color: #c0c4cc;
}

.side-empty i {
  font-size: 40px;
}

.side-empty p {
  font-size: 13px;
  line-height: 1.8;
  margin: 12px 0 0;
}

.side-card {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
}

.card-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.card-title em {
  font-style: normal;
  color: #909399;
  font-weight: 400;
  margin-left: 4px;
}

.card-title--gap {
  margin-top: 12px;
}

/* 标题与来源下拉同行，省一行高度 */
.card-title--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.card-required {
  color: #f56c6c;
  margin-left: 2px;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #e6a23c;
}

.stock-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.stock-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.stock-name {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

.stock-code {
  font-size: 13px;
  color: #909399;
  font-family: Menlo, Consolas, monospace;
}

.stock-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.owned-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.owned-tag {
  cursor: pointer;
}

.owned-empty {
  font-size: 12px;
  color: #c0c4cc;
  line-height: 1.6;
}

/* 景气度 mini 标签（与系统推荐页同视觉） */
.prosp-tags {
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
}

.prosp-tags--left {
  justify-content: flex-start;
  margin-top: 8px;
}

.prosp-tag {
  display: inline-block;
  font-size: 11px;
  line-height: 18px;
  padding: 0 5px;
  border-radius: 3px;
  border: 1px solid #e9e9eb;
  background: #fafafa;
  color: #c0c4cc;
  cursor: default;
  white-space: nowrap;
}

.prosp-tag--positive {
  border-color: #fbc4c4;
  background: #fef0f0;
  color: #f56c6c;
}

.prosp-tag--negative {
  border-color: #b3e19d;
  background: #f0f9eb;
  color: #67c23a;
}

.prosp-tag--neutral {
  border-color: #e9e9eb;
  background: #fafafa;
  color: #909399;
}

.prosp-none {
  color: #c0c4cc;
  font-size: 12px;
}

.theme-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.theme-chip {
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme-none {
  color: #c0c4cc;
  font-size: 12px;
}

/* 审计流：basis 取 0 后按剩余空间生长（高度可预期），
   空间不足时收到 min-height，再不够才让右栏整体滚动 */
.log-card {
  flex: 1 1 0;
  min-height: 92px;
  display: flex;
  flex-direction: column;
}

.log-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.log-item {
  display: flex;
  gap: 8px;
  padding: 7px 2px;
  border-bottom: 1px dashed #f2f6fc;
}

.log-item:last-child {
  border-bottom: none;
}

.log-dot {
  flex: 0 0 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 6px;
  background: #c0c4cc;
}

.log-dot--add { background: #409eff; }
.log-dot--remove { background: #e6a23c; }
.log-dot--create { background: #67c23a; }
.log-dot--merge { background: #9254de; }
.log-dot--deactivate { background: #909399; }
.log-dot--activate { background: #67c23a; }
.log-dot--update { background: #409eff; }

.log-main {
  flex: 1;
  min-width: 0;
}

.log-line {
  font-size: 12px;
  color: #303133;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-action {
  font-weight: 600;
  margin-right: 6px;
}

.log-sub {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 1px;
}

.log-src {
  color: #909399;
}
</style>
